import { Component } from '@theme/component';

/**
 * @typedef {HTMLElement & {
 *   pause?: () => void,
 *   play?: () => void,
 *   resume?: () => void,
 *   suspend?: () => void
 * }} ReviewSlideshow
 */

/**
 * @typedef {object} FeaturedReviewsRefs
 * @property {ReviewSlideshow} slideshow
 */

/**
 * Enhances the shared Horizon slideshow with viewport-aware autoplay and
 * reduced-motion handling for the featured reviews section.
 *
 * @extends {Component<FeaturedReviewsRefs>}
 */
class FeaturedReviews extends Component {
  requiredRefs = ['slideshow'];

  #motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  #pausedForMotion = false;

  #intersectionObserver = new IntersectionObserver(([entry]) => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    if (entry?.isIntersecting && !this.#motionQuery.matches) {
      slideshow.resume?.();
    } else {
      slideshow.suspend?.();
    }
  });

  connectedCallback() {
    super.connectedCallback();

    this.#motionQuery.addEventListener('change', this.#handleMotionPreference);
    this.#intersectionObserver.observe(this);
    this.#applyMotionPreference();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#motionQuery.removeEventListener('change', this.#handleMotionPreference);
    this.#intersectionObserver.disconnect();
  }

  #handleMotionPreference = () => {
    this.#applyMotionPreference();
  };

  #applyMotionPreference() {
    const { slideshow } = this.refs;

    if (!slideshow || !slideshow.hasAttribute('autoplay')) return;

    if (this.#motionQuery.matches) {
      if (!slideshow.hasAttribute('paused')) {
        slideshow.pause?.();
        this.#pausedForMotion = true;
      }
      return;
    }

    if (this.#pausedForMotion) {
      slideshow.play?.();
      this.#pausedForMotion = false;
    }
  }
}

if (!customElements.get('featured-reviews')) {
  customElements.define('featured-reviews', FeaturedReviews);
}
